import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CreateTagPayload,
  Tag,
  UpdateTagPayload,
  createTag,
  deleteTag,
  listTags,
  patchTag,
} from '@/api'

export function useTags(groupId?: string | undefined) {
  const queryClient = useQueryClient()

  // ── List ──────────────────────────────────────────────────────────────────
  const {
    isLoading,
    error,
    isError,
    data: tags = [],
    refetch,
  } = useQuery({
    queryKey: ['tags'],
    retry: 3,
    queryFn: async () => {
      const res = await listTags()
      return res?.data ?? []
    },
  })

  // ── Create ────────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (payload: CreateTagPayload) => createTag(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tags'] }),
  })

  // ── Patch ─────────────────────────────────────────────────────────────────
  const patchMutation = useMutation<
    Tag,
    unknown,
    { id: string; payload: UpdateTagPayload }
  >({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTagPayload }) =>
      patchTag(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tags'] }),
  })

  // ── Delete ────────────────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTag(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tags'] }),
  })

  // const isGeneric = (tag: Tag) => !tag.groupId

  return {
    // Query
    tags,
    isLoading,
    error,
    isError,
    refetch,
    // Mutations
    createTag: createMutation.mutate,
    patchTag: patchMutation.mutate,
    deleteTag: deleteMutation.mutate,
    // Mutation states
    isCreating: createMutation.isPending,
    isPatching: patchMutation.isPending,
    isDeleting: deleteMutation.isPending,
    // Helper
    // isGeneric,
  }
}
